import { useState, useMemo, useCallback, useEffect } from 'react';
import { CombinedCategoryOptions, ListingCategoryConst, parseCombinedCategory, PrefTypeConst } from '../../constants';
import { useAuth } from '../../contexts/AuthContext';
import { useItemFilters } from '../../contexts/ItemFilterContext'; // フィルターContext
import { useMasters } from '../../contexts/MastersContext'; // マスタContext
import { formatFromDateInput } from '../../utils/date';
import { useBiddingItemsGetUpcomingListQuery} from '../../hooks/useBiddingInfoQueryAllRole';
import { BiddingInputFilterDialog } from '../../components/buyer/BiddingInput/BiddingInputFilterDialog'; // フィルターダイアログ
import { ListingScheduleItemRow } from '../../components/admin/ListingSchedule/ListingScheduleItemRow'; // 入札用のItemRow
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useGetBiddingDateForApplyQuery } from '../../hooks/useCalendarQuery';
import { useGetFavoritesQuery } from '../../hooks/useBiddingItemsQuery';
dayjs.extend(utc);
dayjs.extend(timezone);

// デフォルトのタイムゾーンを日本時間(JST)に設定
dayjs.tz.setDefault("Asia/Tokyo");

// # メインのページコンポーネント ---
const ListingSchedulePage = () => {
  //----------------
  // Context使用定義
  //----------------
  const { currentUser } = useAuth();  // 認証context
  const { itemFilters, updateFiltersAndNavigate } = useItemFilters(); // 検索条件context
  const { 
    isMastersLoading, 
    sellers, farmers, teaTypes, teaSeasons, varieties, gaps 
  } = useMasters(); // マスタcontex

  const { data: initialBiddingDate, isLoading: isDateLoading } = useGetBiddingDateForApplyQuery(currentUser?.loginId);
  //初期化完了フラグ
  const [isInitialized, setIsInitialized] = useState(false);

  //----------------
  // State定義
  //----------------
  const [isFilterOpen, setIsFilterOpen] = useState(false);  // 検索ダイアログ表示の状態管理(true:検索ダイアログ表示中)
  const [selectedKey, setSelectedKey] = useState<string | null>(null); // 選択中の明細("itemId_branchNumber" 形式)
  const [displayMode, setDisplayMode] = useState<'all' | 'favorites'>('all');　 // 表示（すべて / お気に入り）の状態管理

  //----------------
  // Memo定義
  //----------------
  const masterDataForComponents = useMemo(() => ({
    varieties, teaTypes, teaSeasons, sellers, farmers, gaps
  }), [varieties, teaTypes, teaSeasons, sellers, farmers, gaps]); // マスタcontextから取得した各マスタデータ

  useEffect(() => {
    if (isDateLoading || isMastersLoading || !currentUser || isInitialized) return;

    const newFilters = { ...itemFilters };
    let hasChanged = false;

    // 未設定なら「次の入札日」を入れる
    if (!newFilters.biddingDate) {
      newFilters.biddingDate = initialBiddingDate ? String(initialBiddingDate) : dayjs().tz().format("YYYYMMDD");
      hasChanged = true;
    }
    // 未設定なら「通常入札(2)・県内(1)」を入れる
    if (!newFilters.listingCategory) {
      newFilters.listingCategory = ListingCategoryConst.BIDDING;
      hasChanged = true;
    }
    if (!newFilters.prefectureType) {
      newFilters.prefectureType = PrefTypeConst.INTERNAL;
      hasChanged = true;
    }

    if (hasChanged) {
      updateFiltersAndNavigate(newFilters);
    }
    // URLへの書き込み命令を出したので準備完了
    setIsInitialized(true);
  }, [initialBiddingDate, isDateLoading, isMastersLoading, currentUser, itemFilters, isInitialized, updateFiltersAndNavigate]);

  //----------------
  // Hooks使用定義
  //----------------
  const isSearchEnabled = !!itemFilters.biddingDate && !!itemFilters.listingCategory;

  // データ取得フック
  const { data: responseData, isLoading: isItemsLoading, error } = useBiddingItemsGetUpcomingListQuery(
    itemFilters, 
    currentUser?.loginId ?? "",
    { enabled: isInitialized } 
  );

  //  お気に入り名簿の取得（フィルタリング用）
  const { data: favorites } = useGetFavoritesQuery(currentUser?.loginId ?? "");

  // 表示用に絞り込んだリストを作成
  const validItems = useMemo(() => {
    if (!responseData || !responseData.items) return [];
    
    let items = responseData.items;

    // 「お気に入り」モードの場合、お気に入りに存在するアイテムのみに絞り込む
    if (displayMode === 'favorites') {
      if (!favorites) return []; // お気に入りが取れていなければ空にする
      items = items.filter(item => 
        favorites.some(f => f.itemId === item.itemId && f.branchNumber === item.branchNumber)
      );
    }

    return items;
  }, [responseData, displayMode, favorites]);

  //----------------
  // CallBack関数定義
  //----------------

  // 選択行を切り替えるハンドラ
  const handleToggleSelectRow = useCallback((itemId: string, branchNumber: number) => {
    const key = `${itemId}_${branchNumber}`;
    setSelectedKey(prevKey => (prevKey === key ? null : key)); // 同じ行なら閉じ、違う行なら開く
  }, []);

  // 手合日フィルターが変更されたときのハンドラ
  const handleBiddingDateChange = useCallback((dateValue: string) => {
    const newDate = formatFromDateInput(dateValue); // "yyyyMMdd"
    // ContextとURLを両方更新
    updateFiltersAndNavigate({
      ...itemFilters, // 他
      biddingDate: newDate,
    });
  }, [itemFilters, updateFiltersAndNavigate]); 
  
    
  // 出品区分と県内県外セレクトボックス
  const handleCombinedCategoryChange = useCallback((selectedValue: string) => {
  const { listingCategory, prefCategory } = parseCombinedCategory(selectedValue);
  
  updateFiltersAndNavigate({
    ...itemFilters,
    listingCategory: listingCategory,
    prefectureType: prefCategory, 
  });
  }, [itemFilters, updateFiltersAndNavigate]);
  //----------------
  // その他定数定義
  //----------------
  const isPageLoading = isMastersLoading || isDateLoading || !isInitialized || (isItemsLoading && !responseData);

  //----------------
  // レンダリング処理
  //----------------
  if (isPageLoading) {
    return ( <div className="... flex items-center justify-center"><p>読み込み中...</p></div> );
  }
  if (error) {
    return ( <div className="... flex items-center justify-center p-4"><div className="bg-red-100 ...">エラー: {error.message}</div></div> );
  }

  // メイン描画
  return (
    <div className="bg-white min-h-screen font-sans">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">      
        {/* --- ヘッダーエリア --- */}
        <header className="mb-8">
            {/* 上段: タイトルと詳細検索ボタン */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-extrabold text-slate-800">出品予定の確認</h1>
                
                {/* 詳細検索ボタン (デザイン変更) */}
                <button
                    onClick={() => setIsFilterOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition border border-indigo-200"
                >
                    詳細検索
                </button>
            </div>

            {/* 下段: ヘッダー検索エリア (グレーのボックス) */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 grid grid-cols-2 gap-4 items-start sm:flex sm:gap-6"> 
              
                {/* 1. 入札日 */}
                <div className="w-full sm:w-auto">
                    <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">入札日</label>
                    <input 
                        type="date" 
                        className="w-full sm:w-48 p-2 h-[42px] border border-slate-300 rounded-md bg-white focus:ring-indigo-500 focus:border-indigo-500 appearance-none safari-date-fix"
                        value={
                          itemFilters.biddingDate 
                            ? dayjs(String(itemFilters.biddingDate).replace(/^(\d{4})(\d{2})(\d{2})$/, "$1-$2-$3")).format("YYYY-MM-DD") 
                            : ""
                        }
                        onChange={(e) => handleBiddingDateChange(e.target.value)}
                    />
                </div>

                {/* 2. 出品区分 */}
                <div className="w-full sm:w-auto">
                    <label className="block text-xs lg:text-sm font-bold text-slate-700 mb-1">出品区分（県内/県外）</label>
                    <select
                        // URLの状態に合わせて選択肢を表示
                        value={
                            itemFilters.listingCategory && itemFilters.prefectureType
                                ? `${itemFilters.listingCategory}_${itemFilters.prefectureType}`
                                : ""
                        }
                        onChange={(e) => handleCombinedCategoryChange(e.target.value)}
                        className="w-full sm:w-56 p-2 h-[42px] border border-slate-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                    >
                        {CombinedCategoryOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                </div>
                {/* 表示対象 */}
                <div className="col-span-2 sm:col-span-1 w-full sm:w-56">
                <label className="block text-sm font-bold text-slate-700 mb-1">表示対象</label>
                <div className="w-full flex items-center justify-around px-2 bg-white h-[42px] rounded-md border border-slate-300 shadow-sm">
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="displayMode"
                      value="all"
                      checked={displayMode === 'all'}
                      onChange={() => setDisplayMode('all')}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-slate-700 whitespace-nowrap">すべて</span>
                  </label>

                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="displayMode"
                      value="favorites"
                      checked={displayMode === 'favorites'}
                      onChange={() => setDisplayMode('favorites')}
                      className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <span className="text-sm font-medium text-slate-700 whitespace-nowrap">お気に入り</span>
                  </label>
                </div>
              </div>
            </div>
        </header>
        <main>
          {!isSearchEnabled ? (
            <div className="text-center py-10 text-gray-500">
              入札日と出品区分を選択してください。
            </div>
          ) : (isItemsLoading && !responseData) ? (
            <div className="text-center py-10 text-gray-500">
              読み込み中...
            </div>
          ) : (responseData && !responseData.isSuccess) ? (
            <div className="text-center py-10 text-gray-500">
              {responseData.message}
            </div>
          ) : (validItems.length === 0) ? (
            <div className="text-center py-10 text-gray-500">
              表示する出品情報がありません。
            </div>
          ) : (
            <div className="space-y-4">
              {validItems.map(item => {
                const rowKey = `${item.itemId}_${item.branchNumber}`; 
                return (
                  <ListingScheduleItemRow 
                    key={rowKey} 
                    item={item} 
                    isSelected={selectedKey === rowKey} 
                    onToggleSelect={() => handleToggleSelectRow(item.itemId, item.branchNumber)}
                  /> 
                );
              })}
            </div>
          )}
        </main>
        
        <BiddingInputFilterDialog 
          isOpen={isFilterOpen} 
          onClose={() => setIsFilterOpen(false)} 
          masterData={masterDataForComponents}
          isSeller = {false}
          isFarmer = {false}
        />
      </div>
    </div>
  );
};

export default ListingSchedulePage;